import React, { useState, useEffect } from "react";
import { Tabs, Card, Row, Col, Radio, Select, Input, Button, Alert } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PayrollSettings = () => {
  const [activeTab, setActiveTab] = useState("paySchedule");
  const [settings, setSettings] = useState(null);
  const [buttonLabel, setButtonLabel] = useState("Save");
  const [formValues, setFormValues] = useState({
    start_month: null,
    start_year: null,
    payout_strategy: "end_of_month",
    fixed_payout_day: null,
    pan_number: "",
    tan_number: "",
    tds_circle_ao_code: "",
    tax_payment_frequency: "Monthly",
    deductor_name: "",
    deductor_designation: "",
  });
  const [showAlert, setShowAlert] = useState(false);

  const currentYear = new Date().getFullYear();
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  

  const { TabPane } = Tabs;
  const { Option } = Select;


  const getLastSixMonths = () => {
    const currentDate = new Date();
    const monthsList = [];
    for (let i = 0; i < 6; i++) {
      const month = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      monthsList.push({
        value: `${month.getMonth() + 1}-${month.getFullYear()}`, // Format as "month-year"
        label: `${months[month.getMonth()]} ${month.getFullYear()}`, // Display as "Month Year"
      });
    }
    return monthsList;
  };
  

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${apiUrl}/payroll/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      if (response.data.status === "success") {
        const data = response.data.data;
        setSettings(data); // Correctly set settings
        setFormValues({
          start_month: data.start_month,
          start_year: data.start_year,
          start_date_formatted: `${months[data.start_month - 1]} ${data.start_year}`, // Formatting
          payout_strategy: data.payout_strategy,
          fixed_payout_day: data.fixed_payout_day,
          pan_number: data.pan_number,
          tan_number: data.tan_number,
          tds_circle_ao_code: data.tds_circle_ao_code,
          tax_payment_frequency: data.tax_payment_frequency,
          deductor_name: data.deductor_name,
          deductor_designation: data.deductor_designation,
        });

   
        
        setButtonLabel("Save Changes");
        setShowAlert(false);
      } else {
        setSettings(null); // Ensure settings are null when no settings exist
        setButtonLabel("Save");
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      setSettings(null); // Ensure settings are null in case of an error
      setButtonLabel("Save");
      setShowAlert(true);
    }
  };
  
  

  const handleInputChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const validateFields = () => {
    const errors = [];
    if (!formValues.start_month) errors.push("Please select a Start Month.");
    if (!formValues.start_year) errors.push("Please select a Start Year.");
    if (!formValues.payout_strategy) errors.push("Please select a Payout Strategy.");
    if (formValues.payout_strategy === "fixed_date" && !formValues.fixed_payout_day) {
      errors.push("Please select a Fixed Payout Day.");
    }

    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;
  
    // Determine API method and endpoint
    const isEdit = settings?.id;
    const apiEndpoint = isEdit
      ? `${apiUrl}/payroll/settings/edit`
      : `${apiUrl}/payroll/settings/add`;
    const apiMethod = isEdit ? "put" : "post";
  
    try {
      const response = await axios({
        method: apiMethod,
        url: apiEndpoint,
        data: formValues,
        headers: { Authorization: `Bearer ${token}` },
      });
  
      toast.success(response.data.message || "Settings saved successfully!");
      fetchSettings(); // Refresh settings after successful save
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings. Please try again.");
    }
  };
  
  
  

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
      
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h3 className="page-title">Payroll Settings</h3>
            </div>
          </div>
        </div>
        {showAlert && (
          <Alert
            message={<span style={{ fontWeight: "bold", fontSize: "18px" }}>Payroll Settings Required</span>}
            description={
              <span style={{ fontSize: "16px" }}>
                Payroll Settings are mandatory to use the Payroll feature. Please configure your settings before proceeding.
              </span>
            }
            type="warning"
            closable
            onClose={() => setShowAlert(false)}
            style={{
              marginBottom: "16px",
              backgroundColor: "#fff4e5", // Lighter warning background
              border: "1px solid #ffa940", // Stronger border for emphasis
              padding: "20px", // Extra padding for prominence
              borderRadius: "8px", // Rounded corners for a polished look
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
            }}
  />
)}

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Pay Schedule" key="paySchedule">
            <Card title="Pay Schedule">
              <Row gutter={[24, 24]}>
                <Col span={24}>
                  <h5>Calculate Monthly Salary Based On</h5>
                  <Radio.Group
                    value={formValues.salary_calculation || "actualDays"}
                    onChange={(e) => handleInputChange("salary_calculation", e.target.value)}
                  >
                    <Radio value="actualDays">Actual Days in a Month</Radio>
                    <Radio value="organizationWorkingDays">
                      Organization Working Days
                      <Input
                        style={{ width: "80px", marginLeft: "8px" }}
                        placeholder="Days"
                        disabled={formValues.salary_calculation !== "organizationWorkingDays"}
                        value={formValues.organization_working_days || ""}
                        onChange={(e) => handleInputChange("organization_working_days", e.target.value)}
                      />
                    </Radio>
                  </Radio.Group>
                </Col>
                <Col span={24}>
                  <h5>Pay On</h5>
                  <Radio.Group
                    value={formValues.payout_strategy || "end_of_month"}
                    onChange={(e) => handleInputChange("payout_strategy", e.target.value)}
                  >
                    <Radio value="end_of_month">The last working day of every month</Radio>
                    <Radio value="fixed_date">
                      Day
                      <Select
                        style={{ width: "80px", marginLeft: "8px" }}
                        disabled={formValues.payout_strategy !== "fixed_date"}
                        value={formValues.fixed_payout_day || undefined}
                        onChange={(value) => handleInputChange("fixed_payout_day", value)}
                      >
                        {[...Array(31).keys()].map((day) => (
                          <Option key={day + 1} value={day + 1}>
                            {day + 1}
                          </Option>
                        ))}
                      </Select>
                      of every month
                    </Radio>
                  </Radio.Group>
                </Col>

                <Col span={4}>
  <h5>Start Your First Payroll</h5>
  {settings?.payroll_count > 0 ? (
    <span>{formValues.start_date_formatted}</span> // Show as label
  ) : (
    <Select
      placeholder="Select a start date"
      value={
        formValues.start_month && formValues.start_year
          ? `${formValues.start_month}-${formValues.start_year}`
          : undefined
      }
      onChange={(value) => {
        const [month, year] = value.split("-");
        handleInputChange("start_month", parseInt(month));
        handleInputChange("start_year", parseInt(year));
      }}
      style={{ width: "100%" }}
    >
      {getLastSixMonths().map((month) => (
        <Option key={month.value} value={month.value}>
          {month.label}
        </Option>
      ))}
    </Select>
  )}
</Col>




                <Col span={24} className="text-center">
                  <Button type="primary" onClick={handleSubmit}>
                    {buttonLabel}
                  </Button>
                </Col>
              </Row>
            </Card>
          </TabPane>
          <TabPane tab="Organization Tax Details" key="taxDetails">
            <Card title="Tax Details">
              <Row gutter={[24, 24]}>
                <Col span={12}>
                  <h5>PAN Number</h5>
                  <Input
                    placeholder="Enter PAN (e.g., AAAAA0000A)"
                    value={formValues.pan_number || ""}
                    onChange={(e) => handleInputChange("pan_number", e.target.value)}
                  />
                </Col>
                <Col span={12}>
                  <h5>TAN Number</h5>
                  <Input
                    placeholder="Enter TAN (optional)"
                    value={formValues.tan_number || ""}
                    onChange={(e) => handleInputChange("tan_number", e.target.value)}
                  />
                </Col>
                <Col span={12}>
                  <h5>Tax Payment Frequency</h5>
                  <Select
                    value={formValues.tax_payment_frequency || "Monthly"}
                    onChange={(value) => handleInputChange("tax_payment_frequency", value)}
                    style={{ width: "100%" }}
                  >
                    <Option value="Monthly">Monthly</Option>
                    <Option value="Quarterly">Quarterly</Option>
                    <Option value="Yearly">Yearly</Option>
                  </Select>
                </Col>
                <Col span={12}>
                  <h5>Deductor's Name</h5>
                  <Input
                    placeholder="Enter Deductor's Name"
                    value={formValues.deductor_name || ""}
                    onChange={(e) => handleInputChange("deductor_name", e.target.value)}
                  />
                </Col>
                <Col span={12}>
                  <h5>Deductor's Designation</h5>
                  <Input
                    placeholder="Enter Deductor's Designation"
                    value={formValues.deductor_designation || ""}
                    onChange={(e) => handleInputChange("deductor_designation", e.target.value)}
                  />
                </Col>
                <Col span={24} className="text-center">
                  <Button type="primary" onClick={handleSubmit}>
                    {buttonLabel}
                  </Button>
                </Col>
              </Row>
            </Card>
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default PayrollSettings;
